import { Switch, Route, useLocation, Redirect } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient, setAuthCallbacks } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AppPage from "@/pages/App";
import MyNFT from "@/pages/MyNFT";
import Explore from "@/pages/Explore";
import OAuthCallback from "@/pages/OAuthCallback";
import Faucet from "@/pages/Faucet";
import TweetoAdmin from "@/pages/TweetoAdmin";
import Navbar from "@/components/Navbar";
import ScrollToTop from "@/components/ScrollToTop";
import ProjectsPage from "./pages/XDao/ProjectsPage";
import SidebarWalletManagement from "./components/SidebarWalletManagement";
import ImageModal from "@/components/ImageModal";
import CodeVerificationModal from "@/components/CodeVerificationModal";
import { useAuthStore } from "./hooks/useAuthStore";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useEffect } from "react";
import { WalletProvider } from "./providers/WalletProvider";
import * as Sentry from "@sentry/react";
import XDao from "./pages/XDao";
import useWalletModalStore from '@/stores/useWalletModalStore';
import WalletModal from "./components/WalletModal";
import { useShallow } from "zustand/react/shallow";
import LoginModal from "./components/LoginModal";
import { useRestoreAuthSession } from "./hooks/useRestoreAuthSession";

function Router() {
  const [location, setLocation] = useLocation();
  const { isAuthenticated, sessionValidated } = useAuthStore(useShallow(state => ({
    isAuthenticated: state.isAuthenticated,
    sessionValidated: state.sessionValidated
  })));
  const logout = useAuthStore(state => state.logout);
  const showNavbar = location !== "/" && location !== "/login" && location !== "/oauth/callback" && location !== "/official-link" && location !== "/404";
  const { visible: isShowWalletModal } = useWalletModalStore();

  useRestoreAuthSession();
  const isAdminRoute = location.startsWith("/tweeto-admin");

  // Set up global auth callbacks for 401 handling
  useEffect(() => {
    setAuthCallbacks(logout, setLocation);
  }, [logout, setLocation]);

  return (
    <div className="min-h-screen bg-black text-white">
      {showNavbar && !isAdminRoute && <Navbar />}
      <Switch>
        <Route path="/">
          <Redirect to="/xdao" />
        </Route>
        <Route path="/official-link" component={Landing} />
        <Route path="/login" component={Login} />
        <Route path="/oauth/callback" component={OAuthCallback} />

        {/* Protected Routes */}
        <Route path="/xdao/:slug">
          {/* <ProtectedRoute> */}
            <ProjectsPage />
          {/* </ProtectedRoute> */}
        </Route>
        <Route path="/xdao">
          {/* <ProtectedRoute> */}
            <XDao/>
          {/* </ProtectedRoute> */}
        </Route>
        <Route path="/app">
          <ProtectedRoute>
            <AppPage />
          </ProtectedRoute>
        </Route>
        <Route path="/my-nfts">
          <ProtectedRoute>
            <MyNFT />
          </ProtectedRoute>
        </Route>
        <Route path="/explore">
          <ProtectedRoute>
            <Explore />
          </ProtectedRoute>
        </Route>

        <Route path="/tweeto-admin">
          <TweetoAdmin />
        </Route>
        <Route path="/tweeto-admin/*?">
          <TweetoAdmin />
        </Route>

        {/* Faucet Route - Only show in development */}
        <Route path="/xdao/:slug/faucet">
          <ProtectedRoute>
            <Faucet />
          </ProtectedRoute>
        </Route>

        {/* 404 Page */}
        <Route path="/404" component={NotFound} />

        {/* Catch-all for undefined routes */}
        <Route>
          <Redirect to="/404" />
        </Route>
      </Switch>
      {(showNavbar && !isAdminRoute && sessionValidated && isAuthenticated) && <SidebarWalletManagement />}
      {(isShowWalletModal && !isAdminRoute && sessionValidated && isAuthenticated) && <WalletModal />}
      {!isAuthenticated && !isAdminRoute && <LoginModal />}
      <ScrollToTop />
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center space-y-4">
            <h1 className="text-2xl font-bold text-red-500">Something went wrong</h1>
            <p className="text-gray-400">
              We've been notified about this error and will look into it.
            </p>
            <button
              onClick={resetError}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      )}
      showDialog={false}
    >
      <QueryClientProvider client={queryClient}>
        <WalletProvider>
          <TooltipProvider>
            <Router />
            <Toaster />
            <ImageModal />
            <CodeVerificationModal />
          </TooltipProvider>
        </WalletProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
